import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Alert,
  useTheme,
} from '@mui/material';
import { Close, Visibility } from '@mui/icons-material';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';

interface AdPreviewModalProps {
  open: boolean;
  onClose: () => void;
  materialId: number;
  productName?: string;
}

const AdPreviewModal: React.FC<AdPreviewModalProps> = ({
  open,
  onClose,
  materialId,
  productName,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [previewHtml, setPreviewHtml] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && materialId) {
      fetchPreview();
    }
  }, [open, materialId]);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = (await fetchData(
        `/api/ad-statistics/preview/${materialId}`,
        {},
        false,
      )) as any;

      if (response.success && response.html) {
        setPreviewHtml(response.html);
      } else {
        setError('Vorschau konnte nicht geladen werden');
      }
    } catch (err: any) {
      console.error('Error fetching preview:', err);
      if (err.message?.includes('404')) {
        setError('Material nicht gefunden');
      } else if (err.message?.includes('408')) {
        setError('Zeitüberschreitung beim Laden der Vorschau');
      } else {
        setError('Vorschau nicht verfügbar');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPreviewHtml('');
    setError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Visibility fontSize="small" />
            <Typography variant="h6">
              Material-Vorschau - ID {materialId}
              {productName && ` - ${productName}`}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box
            sx={{
              '& img': {
                maxWidth: '100%',
                height: 'auto',
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'underline',
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
              },
              '& td, & th': {
                padding: '8px',
                textAlign: 'left',
              },
              // Style for SAM metadata box
              '& div[style*="background: #f0f0f0"]': {
                backgroundColor: colors.grey[700],
                color: colors.grey[100],
                borderColor: colors.grey[600],
              },
              // General text styling
              color: colors.grey[100],
              '& p, & div, & span': {
                color: 'inherit',
              },
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdPreviewModal;
